import { useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import type { MessageKey } from '@bookingdei/i18n/message-key';
import { api } from '../api';
import { useLanguage } from './language-context';
import { useSearchHint } from './search-hint';
import { useLayer, LayerScrim } from './layer-stack';
import { Icon } from './icon';
import type { IconName } from './icon';
import { paletteItems } from './nav-items';
import { surfaceOrigin } from './surface-links';
import type { BookingList } from '../mock/seeds/schema-types';
import type { components } from '@bookingdei/api-client/types';
import './command-palette.css';

// ⌘K command palette (CB-17; Others.dc.html L191-222, script L1340-1400). One search
// over three kinds, as in the design: the customer's bookings (GET /v1/me/bookings),
// businesses (GET /v1/search/suggest — kind `business`, opened on the website's business
// page) and the dashboard's pages. Empty query lists bookings then pages; typing
// filters bookings/pages locally (every word must match) and asks /search/suggest for
// businesses. ↑/↓ move, Enter opens, hover selects, Esc closes (layer stack).

type SuggestResults = components['schemas']['SuggestResults'];

interface PaletteRow {
  id: string;
  kind: 'booking' | 'business' | 'page';
  icon: IconName;
  label: string;
  sub: string;
  hay: string;
  go: () => void;
}

const KIND_KEY: Record<PaletteRow['kind'], MessageKey> = {
  booking: 'user.shell.palette.kind.booking',
  business: 'user.shell.palette.kind.business',
  page: 'user.shell.palette.kind.page',
};

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t, lang } = useLanguage();
  const searchHint = useSearchHint();
  const navigate = useNavigate();
  const { z } = useLayer(open, onClose, { kind: 'sheet', history: false });
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const [bookings, setBookings] = useState<BookingList['items']>([]);
  const [businesses, setBusinesses] = useState<SuggestResults['items']>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fresh state + the bookings index each time the palette opens.
  useEffect(() => {
    if (!open) return;
    setQ('');
    setSel(0);
    setBusinesses([]);
    inputRef.current?.focus();
    const controller = new AbortController();
    api
      .GET('/me/bookings', { signal: controller.signal })
      .then((res) => setBookings((res as BookingList).items ?? []))
      .catch(() => undefined);
    return () => controller.abort();
  }, [open]);

  // Businesses from /search/suggest (debounced; the endpoint is prefix search, ≥2 chars).
  const query = q.trim();
  useEffect(() => {
    if (!open || query.length < 2) {
      setBusinesses([]);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      api
        .GET('/search/suggest', { params: { query: { q: query, lang } }, signal: controller.signal })
        .then((res) => setBusinesses(((res as SuggestResults).items ?? []).filter((s) => s.kind === 'business' && s.ref)))
        .catch(() => undefined);
    }, 160);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [open, query, lang]);

  const rows = useMemo<PaletteRow[]>(() => {
    const close = (fn: () => void) => () => {
      onClose();
      fn();
    };
    const local: PaletteRow[] = [
      ...bookings.map<PaletteRow>((b) => {
        const service = b.items?.[0]?.name_snapshot ?? '';
        const biz = b.business?.name ?? '';
        return {
          id: `booking:${b.code}`,
          kind: 'booking',
          icon: 'bookings',
          label: service || biz,
          sub: [biz, b.code].filter(Boolean).join(' · '),
          hay: [service, biz, b.code].join(' ').toLowerCase(),
          go: close(() => navigate(`/bookings?b=${encodeURIComponent(b.code)}`)),
        };
      }),
      ...paletteItems.map<PaletteRow>((item) => {
        const label = t(item.key);
        return {
          id: `page:${item.id}`,
          kind: 'page',
          icon: item.icon,
          label,
          sub: t('user.shell.palette.go_to_page'),
          hay: `${label} ${item.id}`.toLowerCase(),
          go: close(() => navigate(item.to)),
        };
      }),
    ];
    if (!query) return local.slice(0, 6);
    const words = query.toLowerCase().split(/\s+/);
    const hits = local.filter((r) => words.every((w) => r.hay.includes(w)));
    const website = surfaceOrigin('website') ?? '';
    const biz = businesses.map<PaletteRow>((s) => ({
      id: `business:${s.ref}`,
      kind: 'business',
      icon: 'saved',
      label: s.text,
      sub: t('user.shell.palette.business_page'),
      hay: s.text.toLowerCase(),
      go: close(() => window.location.assign(`${website}/b/${encodeURIComponent(s.ref ?? '')}`)),
    }));
    return [...hits, ...biz].slice(0, 7);
  }, [bookings, businesses, query, t, navigate, onClose]);

  if (!open) return null;

  const active = rows.length ? Math.min(sel, rows.length - 1) : 0;

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!rows.length) return;
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setSel((active + step + rows.length) % rows.length);
    } else if (e.key === 'Enter') {
      const hit = rows[active];
      if (hit) {
        e.preventDefault();
        hit.go();
      }
    }
  };

  return (
    <LayerScrim z={z} onClose={onClose} className="palette-scrim">
      <div className="palette" role="dialog" aria-modal="true" aria-label={searchHint} data-motion="">
        <div className="palette__bar">
          <span className="palette__glass">
            <Icon name="search" size={18} stroke={1.7} />
          </span>
          <input
            ref={inputRef}
            className="palette__input"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setSel(0);
            }}
            onKeyDown={onKeyDown}
            placeholder={searchHint}
            aria-label={searchHint}
            role="combobox"
            aria-expanded="true"
            aria-controls="palette-results"
            aria-activedescendant={rows[active] ? `palette-row-${active}` : undefined}
            autoComplete="off"
            spellCheck={false}
          />
          <span className="palette__key">{t('user.shell.palette.esc')}</span>
        </div>
        <div className="palette__results" id="palette-results" role="listbox" aria-label={t('user.shell.palette.results')}>
          {rows.map((r, i) => (
            <div
              key={r.id}
              id={`palette-row-${i}`}
              role="option"
              aria-selected={i === active}
              className={i === active ? 'palette__row is-active' : 'palette__row'}
              onMouseEnter={() => setSel(i)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={r.go}
            >
              <span className="palette__row-icon">
                <Icon name={r.icon} size={16} stroke={1.7} />
              </span>
              <span className="palette__row-text">
                <span className="palette__row-label">{r.label}</span>
                <span className="palette__row-sub">{r.sub}</span>
              </span>
              <span className="palette__row-kind">{t(KIND_KEY[r.kind])}</span>
            </div>
          ))}
          {query.length > 0 && rows.length === 0 && (
            <div className="palette__empty">
              <span className="palette__empty-title">{t('user.shell.palette.empty_title')}</span>
              <span className="palette__empty-sub">{t('user.shell.palette.empty_sub')}</span>
            </div>
          )}
        </div>
        <div className="palette__hints">
          <span>{t('user.shell.palette.hint_move')}</span>
          <span>{t('user.shell.palette.hint_open')}</span>
        </div>
      </div>
    </LayerScrim>
  );
}
